import { useOrderingMessage } from "../useOrderingMessage";
import {
  MenuOutlined,
  MessageOutlined,
  SelectOutlined,
  ShoppingOutlined,
} from "@ant-design/icons";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { BillElementItemOut, BillOut } from "../../../../api/bills";
import { useUpdateItemServed } from "../../../../hooks/useBills";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../../hooks/useViewport";
import { useIsEdgeLocal } from "../../../../hooks/useEdgeMode";
import { TablesEmptyState } from "./TablesEmptyState";
import { OrderItemRow } from "./OrderItemRow";
import { StatusLegendHint } from "./StatusLegendHint";
import { SummarizedCheckbox } from "./SummarizedCheckbox";
import {
  buildSummarizedRows,
  sortBillElementsForDisplay,
  toDisplayRow,
} from "./billSummaryUtils";
import { BillCustomerBanner } from "./clients/BillCustomerBanner";
import { BillReservationBanner } from "./seat/BillReservationBanner";

interface OrderDetailsProps {
  bill: BillOut | null;
  selectedItemId?: number | null;
  onItemSelect?: (itemId: number | null) => void;
  /** Отваря релсата с маси на тесен екран — подава се от родителя при нужда. */
  onShowTables?: () => void;
  /** Двоен клик на ред — бърза повторна поръчка на продукта (подава product id). */
  onItemReorder?: (productId: number, lunchMenuId?: number | null) => void;
  /** „Обобщено" — държи се от родителя (там е и изборът на ред). На десктоп
   *  чекбоксът е долу вдясно, точно над лентата с бутоните; на телефон — на
   *  първия разделител. */
  summarized: boolean;
  onToggleSummarized: () => void;
}

export const OrderDetails = ({
  bill,
  selectedItemId: externalSelectedItemId,
  onItemSelect,
  onShowTables,
  onItemReorder,
  summarized,
  onToggleSummarized,
}: OrderDetailsProps) => {
  const { t } = useTranslation();
  const message = useOrderingMessage();
  const { isDesktop } = useViewport();
  // Мобилен stack се ползва до първия breakpoint над 1000px (isDesktop = ≥1024).
  // НЕ се казва isNarrow: споделеният isNarrow от useViewport значи <640px.
  const isCompactLayout = !isDesktop;
  const edgeLocal = useIsEdgeLocal();
  const updateItemServedMutation = useUpdateItemServed();
  const [internalSelectedItemId, setInternalSelectedItemId] = useState<
    number | null
  >(null);

  const selectedItemId =
    externalSelectedItemId !== undefined
      ? externalSelectedItemId
      : internalSelectedItemId;

  // Дълга сметка се показва от КРАЯ — там е последната поръчка. Скролира се
  // при смяна на масата и когато се появят нови редове (нова поръчка);
  // опресняване без нови редове (сервиране, статус от кухнята, WS) не мърда
  // списъка, за да не дърпа сервитьора, докато го разглежда.
  const listRef = useRef<HTMLDivElement>(null);
  const billId = bill?.id ?? null;
  const itemCount =
    bill?.elements.reduce((sum, el) => sum + el.items.length, 0) ?? 0;
  const previousCountRef = useRef<{ billId: number | null; count: number }>({
    billId: null,
    count: 0,
  });
  useLayoutEffect(() => {
    const previous = previousCountRef.current;
    previousCountRef.current = { billId, count: itemCount };
    const shouldScroll =
      billId !== previous.billId || itemCount > previous.count;
    if (!shouldScroll || !listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [billId, itemCount]);

  // Избраният ред (вкл. със стрелките ↑ ↓) се довежда във видимата част.
  const rowRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  useEffect(() => {
    if (selectedItemId == null) return;
    rowRefs.current
      .get(selectedItemId)
      ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [selectedItemId]);

  const toggleItemSelection = (itemId: number) => {
    const newValue = selectedItemId === itemId ? null : itemId;
    if (onItemSelect) onItemSelect(newValue);
    else setInternalSelectedItemId(newValue);
  };

  const handleToggleServed = async (
    billId: number,
    billElementId: number,
    item: BillElementItemOut,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();
    // Toggle: несервиран ред се сервира от всеки статус; втори клик връща
    // served=false и редът пак показва ТЕКУЩИЯ кухненски статус (той може
    // междувременно да е мръднал — кухнята не спира да готви).
    const nextServed = !item.served;
    try {
      // Сервирането е ОТДЕЛНА ос (bill_element_items.served), не кухненски
      // статус — не пипа какво виждат мониторите/шпикерът.
      await updateItemServedMutation.mutateAsync({
        billId,
        billElementId,
        billElementItemId: item.id,
        served: nextServed,
      });
      message.success(
        t(nextServed ? "ordering.itemServed" : "ordering.itemUnserved"),
      );
    } catch {
      message.error(t("ordering.itemServedError"));
    }
  };

  if (!bill) {
    return (
      <div className="h-full flex items-center justify-center p-6">
        <TablesEmptyState
          icon={<SelectOutlined />}
          title={t("ordering.noTableSelected")}
          description={t("ordering.noTableSelectedHint")}
        />
      </div>
    );
  }

  // Днешна партида НЕ носи етикет „Днес" — показва се само часът.
  const formatDate = (dateString: string): string | null => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const same = (d1: Date, d2: Date) =>
      d1.getDate() === d2.getDate() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getFullYear() === d2.getFullYear();
    if (same(date, today)) return null;
    if (same(date, yesterday)) return t("common.yesterday");
    return date.toLocaleDateString("bg-BG", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("bg-BG", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatBatchStamp = (dateString: string) => {
    const datePart = formatDate(dateString);
    const timePart = formatTime(dateString);
    return datePart ? `${datePart} · ${timePart}` : timePart;
  };

  // Партиди без редове не се показват; изцяло сторнирана сметка изглежда празна.
  const sortedElements = sortBillElementsForDisplay(bill.elements);

  const hasItems = sortedElements.length > 0;

  // На телефон чекбоксът седи на линията на разделителя, без собствен ред
  const narrowToggle = isCompactLayout && hasItems && (
    <SummarizedCheckbox
      checked={summarized}
      onToggle={onToggleSummarized}
      small
    />
  );

  return (
    <div className="h-full flex flex-col text-pos-ink relative overflow-hidden">
      {/* Хедър: маса · обща сума — САМО на телефон при скрита релса с маси
          (иначе дублира избраната карта вляво) */}
      {onShowTables && isCompactLayout && (
        <div className="border-b border-pos-line2 flex items-baseline gap-2.5 px-3 pt-2.5 pb-2">
          <button
            type="button"
            onClick={onShowTables}
            className="w-[30px] h-[30px] rounded-[9px] border border-pos-ctl bg-pos-card grid place-items-center text-pos-accent self-center shrink-0 cursor-pointer"
          >
            <MenuOutlined className="text-sm" />
          </button>
          <span
            title={bill.name}
            className="min-w-0 truncate font-extrabold text-pos-ink text-base"
          >
            {bill.name}
          </span>
          <span className="ml-auto shrink-0 font-pos-mono font-bold text-pos-ink text-[15px]">
            {parseFloat(bill.total_amount).toFixed(2)} EUR
          </span>
        </div>
      )}

      {/* Настанена резервация — над списъка с поръчаното, като клиента. В
          локален режим полето е остаряло копие от облака (TableList също не
          го показва там). */}
      {bill.reservation && !edgeLocal && (
        <BillReservationBanner
          reservation={bill.reservation}
          compact={isCompactLayout}
          className={isCompactLayout ? "px-3" : "px-[22px]"}
        />
      )}

      {/* Закачен клиент — над списъка с поръчаното, извън скрола, за да се
          вижда винаги; на тесен екран под хедъра маса · сума */}
      {bill.discount_applied && (
        <BillCustomerBanner
          bill={bill}
          compact={isCompactLayout}
          // Същият хоризонтален падинг като скрол зоната с редовете
          className={isCompactLayout ? "px-3" : "px-[22px]"}
        />
      )}

      {/* Скрол зоната е в собствен relative wrapper заедно с водния знак */}
      <div className="relative flex-1 min-h-0 flex flex-col overflow-hidden">
        {/* Watermark долу-дясно — както иконите в картите на Модули: едва
            доловим, зад редовете (скрол контейнерът е relative и го покрива) */}
        <span
          aria-hidden
          className="pointer-events-none select-none absolute bottom-4 right-6 font-extrabold tracking-tight text-[length:min(10vw,140px)] leading-none text-pos-accent/[.07]"
        >
          UnrealSoft
        </span>
        <div
          ref={listRef}
          className={`relative flex-1 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden pt-3 pb-5 flex flex-col gap-3.5 ${
            isCompactLayout ? "px-3" : "px-[22px]"
          }`}
        >
        {!hasItems ? (
          <div className="flex items-center justify-center pt-10">
            <TablesEmptyState
              icon={<ShoppingOutlined />}
              title={t("ordering.noOrdersForTable")}
              description={t("ordering.noOrdersForTableHint")}
            />
          </div>
        ) : isCompactLayout ? (
          /* Телефон: ЕДИН разделител — часът на първата поръчка вляво,
             „Обобщено" + легендата вдясно. На десктоп заглавна секция НЯМА
             (махната по искане) — там часът е в батч разделителя, а
             легендата — на първия от тях. */
          <div className="flex items-center min-w-0 gap-2.5">
            <span className="shrink-0 font-pos-mono text-[11px] tracking-[.09em] uppercase text-pos-muted">
              {formatBatchStamp(sortedElements[0].order_time)}
            </span>
            <span aria-hidden className="flex-1 h-px bg-pos-line2" />
            {narrowToggle}
            <StatusLegendHint />
          </div>
        ) : null}
        {!hasItems ? null : summarized ? (
          /* ОБОБЩЕНО: общ списък без часови разделители; еднаквите редове са
             слети, затова не са избираеми и лентата не сервира. */
          <section>
            <div className="flex flex-col gap-1.5">
              {buildSummarizedRows(sortedElements).map((row) => (
                <OrderItemRow
                  key={row.key}
                  row={row}
                  isCompactLayout={isCompactLayout}
                  isSelected={false}
                  onDoubleClick={
                    onItemReorder
                      ? () => onItemReorder(row.item.item_id, row.item.lunch_menu_id)
                      : undefined
                  }
                />
              ))}
            </div>
          </section>
        ) : (
          sortedElements.map((element, elementIndex) => (
            <section key={element.id}>
              {/* Батч хедър: дата · час + коментар chip — за всяка поръчка;
                  на телефон първата е БЕЗ него (часът ѝ е в общия разделител
                  на хедъра), освен ако няма коментар за показване */}
              {(!isCompactLayout || elementIndex > 0 || element.comment) && (
              <div className="flex items-center gap-2.5 mb-2 min-w-0">
                {(!isCompactLayout || elementIndex > 0) && (
                  <span className="shrink-0 font-pos-mono text-[11px] tracking-[.09em] uppercase text-pos-muted">
                    {formatBatchStamp(element.order_time)}
                  </span>
                )}
                  {/* Коментарът към поръчката е ЧЕРВЕН (danger токените) —
                      да се набива на очи, като коментара към продукт */}
                  {element.comment && (
                    <span className="min-w-0 inline-flex items-start gap-1 bg-pos-danger-bg border border-pos-danger-brd text-pos-danger-ink font-semibold italic rounded-[7px] px-1.5 py-0.5 text-[11px] normal-case">
                      <MessageOutlined className="text-[10px] shrink-0 mt-[3px]" />
                      <span className="min-w-0 break-words">
                        {element.comment}
                      </span>
                    </span>
                  )}
                <span className="flex-1 min-w-2 h-px bg-pos-line2" />
                {!isCompactLayout && elementIndex === 0 && <StatusLegendHint />}
              </div>
              )}

              <div className="flex flex-col gap-1.5">
                {element.items.map((item) => (
                  <OrderItemRow
                    key={item.id}
                    rowRef={(rowElement) => {
                      if (rowElement) rowRefs.current.set(item.id, rowElement);
                      else rowRefs.current.delete(item.id);
                    }}
                    row={toDisplayRow(item)}
                    isCompactLayout={isCompactLayout}
                    isSelected={selectedItemId === item.id}
                    onClick={() => toggleItemSelection(item.id)}
                    onDoubleClick={
                      onItemReorder
                        ? () => onItemReorder(item.item_id, item.lunch_menu_id)
                        : undefined
                    }
                    onServe={(e) =>
                      handleToggleServed(bill.id, element.id, item, e)
                    }
                  />
                ))}
              </div>
            </section>
          ))
        )}
        </div>
      </div>

      {/* Десктоп: „Обобщено" долу вдясно, над лентата — извън нея, за да остане
          цялата лента за бутоните. Редът стои и без поръчки (без чекбокс), за
          да не подскача списъкът; височината съвпада с часовника вляво
          (`TableRail`). */}
      {!isCompactLayout && (
        <div className="shrink-0 flex h-12 items-center justify-end px-[22px]">
          {hasItems && (
            <SummarizedCheckbox
              checked={summarized}
              onToggle={onToggleSummarized}
            />
          )}
        </div>
      )}
    </div>
  );
};
